import { toast } from 'react-toastify';
import { CmmFile } from '../../../shared/types/cmmReport.ts';
import { evalImportPositions } from '../../services/cmm-import/evalImportPositions.ts';
import { importCmmFiles } from '../../services/cmm-import/importCmmFiles.ts';
import { CmmPositionRead, FileSource } from '../../services/cmm-import/types.ts';
import { CmmConfig, Protocol, RecordDraft, RecordPosition } from '../../types/sharedTypeImpl.ts';
import { countRecordValuesFilled } from '../../utils/countRecordValuesFilled.ts';
import { shouldArchiveCmmReports } from '../../utils/shouldArchiveCmmReports.ts';
import { trpc } from '../../utils/trpc/trpc.ts';
import { trpcClient } from '../../utils/trpc/trpc.ts';

interface Props {
    protocol: Protocol;
    record: RecordDraft;
    partIndex: number;
    cmmConfigs: CmmConfig[] | undefined;
    setRecord: (record: RecordDraft) => void;
    setIsDirty: (isDirty: boolean) => void;
}

export function useCmmValuesApply({ protocol, record, partIndex, cmmConfigs, setRecord, setIsDirty }: Props) {
    const { data: cmmEquipments } = trpc.equipments.getAllCustom.useQuery({ type: 'cmm/vmm' });

    async function attemptApplyAutoImport(fileSource: FileSource, cmmReport: CmmFile) {
        if (!cmmConfigs) {
            alert('Failed to load CMM configurations');
            return false;
        }

        try {
            const importResults = await importCmmFiles(
                protocol,
                record,
                partIndex,
                cmmConfigs,
                [cmmReport.file],
                fileSource,
                {
                    ignoreInvalidPosNr: true,
                },
                cmmEquipments,
            );
            const hasIssues = !importResults[0].isIncluded || importResults[0].positions?.some((it) => !it.isIncluded);
            if (hasIssues) {
                return false;
            }

            const importResult = importResults[0];
            applyImportValues(importResult.positions!, [cmmReport.name], true);
            return true;
        } catch (error) {
            alert('Failed to import CMM report.');
            console.error('attemptApplyAutoImport error:', error);
            return false;
        }
    }

    function applyImportValues(importValues: RecordPosition[], sharePointFiles: string[], showToast: boolean = false) {
        setImportedValues(importValues);
        archiveImportedFiles(sharePointFiles);
        if (showToast) {
            toast.success('CMM report imported successfully');
        }
    }

    const setImportedValues = (importValues: RecordPosition[]) => {
        const newRecord = structuredClone(record);
        for (const importValue of importValues) {
            const position = protocol.positions.find((it) => it.positionNumber === importValue.positionNumber);
            if (!position || position.type !== 'measurement') continue;

            const part = newRecord.parts[partIndex];
            const positionValue = part.positionValues.find((it) => it.positionNumber === importValue.positionNumber);
            if (positionValue == null) continue;

            positionValue.value = importValue.value;
            positionValue.equipmentId = importValue.equipmentId;
        }
        newRecord.numValuesFilled = countRecordValuesFilled(newRecord);

        setRecord(newRecord);
        setIsDirty(true);
    };

    const archiveImportedFiles = (sharePointFiles: string[]) => {
        if (sharePointFiles == null || sharePointFiles.length === 0) return;
        if (!shouldArchiveCmmReports()) {
            console.log('Skipped archiving CMM reports: ', sharePointFiles);
            return;
        }

        try {
            trpcClient.cmmReports.archive.mutate({ fileNames: sharePointFiles });
        } catch (error) {
            alert('Failed to archive some imported files');
        }
    };

    /**
     * Applies multiple part groups from a multi-part Excel file into consecutive record parts.
     * @param partGroups Raw position reads per part (from splitIntoPartGroups).
     * @param equipmentId The CMM equipment id to stamp on each imported value.
     * @param startPartIndex 0-based index of the first record part to write into.
     * @param expandToFit  If true, blank parts are appended to the record to accommodate all groups.
     * @param includePartiallyFailed If true, parts with some failed positions are included.
     * @param sharePointFiles File names to archive after successful import.
     */
    async function applyMultiPartValues(
        partGroups: CmmPositionRead[][],
        equipmentId: string,
        startPartIndex: number,
        expandToFit: boolean,
        includePartiallyFailed: boolean,
        sharePointFiles: string[],
    ) {
        const newRecord = structuredClone(record);
        const evalPartIndex = Math.min(startPartIndex, Math.max(0, newRecord.parts.length - 1));

        const importablePartGroups = partGroups
            .map((group) => ({
                group,
                evaluated: evalImportPositions(protocol, newRecord, evalPartIndex, group),
            }))
            .filter((partGroup) => {
                const importableCount = partGroup.evaluated.filter((p) => p.isImportable).length;
                if (importableCount === 0) return false; // fully failed
                if (!includePartiallyFailed && partGroup.group.some((p) => p.issue)) return false; // partially failed
                return true;
            });

        const neededCount = startPartIndex + importablePartGroups.length;

        // Expand record.parts so every group has a target slot
        if (expandToFit && neededCount > newRecord.parts.length) {
            while (newRecord.parts.length < neededCount) {
                newRecord.parts.push({
                    positionValues: protocol.positions.map((it) => ({
                        positionNumber: it.positionNumber,
                        value: null,
                    })),
                });
            }
        }

        // Write each part group into its slot
        for (let i = 0; i < importablePartGroups.length; i++) {
            const recordPartIndex = startPartIndex + i;
            if (recordPartIndex >= newRecord.parts.length) break;

            const evaluated = evalImportPositions(protocol, newRecord, recordPartIndex, importablePartGroups[i].group);
            const part = newRecord.parts[recordPartIndex];

            for (const importedPos of evaluated) {
                if (!importedPos.isImportable) continue; // skip Fail / invalid / visual inspection

                const position = protocol.positions.find((it) => it.positionNumber === importedPos.positionNumber);
                if (!position || position.type !== 'measurement') continue;

                const positionValue = part.positionValues.find((it) => it.positionNumber === importedPos.positionNumber);
                if (positionValue == null) continue;

                positionValue.value = importedPos.value;
                positionValue.equipmentId = equipmentId;
            }
        }

        newRecord.numValuesFilled = countRecordValuesFilled(newRecord);
        setRecord(newRecord);
        setIsDirty(true);

        archiveImportedFiles(sharePointFiles);
        toast.success(`Imported ${importablePartGroups.length} part${importablePartGroups.length !== 1 ? 's' : ''} from CMM report`);
    }

    return {
        attemptApplyAutoImport,
        applyImportValues,
        applyMultiPartValues,
    };
}
